import PropTypes from 'prop-types';
import { configActions } from 'core/Config';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import { validationSchema } from 'shared/utils';
import {
    ButtonFooter,
    Container,
    ExpandButton,
    FormGroup,
    Input,
    Modal,
    TagList,
    Text
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import React, { useEffect } from 'react';
import {
    useArray,
    useDispatch,
    useErrors,
    useInput,
    useObjectState,
    useWindowSize
} from 'hooks';

const DEFAULT_FORM = {
    doctor_id: null,
    enabled: true,
    end_time: '',
    mean: 'email',
    prev_minutes: '',
    specialty_id: null,
    start_time: '',
    type: null
};

const ConfigTemplateModal = ({
    active,
    data = {},
    onToggle
}) => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { xs } = useWindowSize();
    const { clearError, errors, setErrors } = useErrors();
    const [currentOptions, setCurrentOptions, { push: pushOption, remove: removeOption }] = useArray([]);
    const [form, setForm, setFormValue] = useObjectState({ ...DEFAULT_FORM });
    const [optionInput, optionText, setOptionText] = useInput('');

    const createMode = !data?.id;

    useEffect(() => {
        if (isEmpty(data)) return;
        setOptionText('');
        if (!data?.id) {
            setForm({ ...DEFAULT_FORM });
            setCurrentOptions([]);
            return;
        }
        setForm(data);
        setCurrentOptions(data?.options ?? []);
    }, [data, setForm, setOptionText]);

    const modalProps = {
        active,
        onToggle,
        width: '100%'
    };

    const handleOptionAdd = () => {
        if (!optionText.trim()) return;
        pushOption(optionText);
        setOptionText('');
    };

    const onSubmit = () => {
        const { name, subject } = form;
        const { errors, isValid } = validationSchema({
            name: [!!name, t('Requerido')],
            subject: [!!subject, t('Requerido')]
        });
        setErrors(errors);
        if (!isValid) return;
        const formData = {
            ...form,
            options: currentOptions
        };
        if (createMode) {
            dispatch(configActions.createTemplate(formData));
        } else {
            dispatch(configActions.updateTemplate(formData));
        }
        onToggle();
    };

    const parsedOptions = currentOptions.map(o => ({ label: o }));

    return (
        <Modal { ...modalProps }>
            <Row>
                <Text bold margin="0px 0px 10px 0px">
                    { t('Nombre') }
                </Text>
                <Col xs="12">
                    <FormGroup label="Default" margin="0px 0px 10px 0px">
                        <Input
                            error={ errors?.name }
                            value={ form.name ?? '' }
                            onChange={ value => {
                                clearError('name');
                                setFormValue('name', value);
                            } }
                        />
                    </FormGroup>
                </Col>
                <Col xs="12">
                    <FormGroup label={ t('Español') } margin="0px 0px 10px 0px">
                        <Input
                            value={ form.es ?? '' }
                            onChange={ value => setFormValue('es', value) }
                        />
                    </FormGroup>
                </Col>
                <Col xs="12">
                    <FormGroup label={ t('Inglés') } margin="0px 0px 10px 0px">
                        <Input
                            value={ form.en ?? '' }
                            onChange={ value => setFormValue('en', value) }
                        />
                    </FormGroup>
                </Col>
                <Text bold margin="0px 0px 10px 0px">
                    { t('Asunto') }
                </Text>
                <Col xs="12">
                    <FormGroup label="Default" margin="0px 0px 10px 0px">
                        <Input
                            error={ errors?.subject }
                            value={ form.subject ?? '' }
                            onChange={ value => {
                                clearError('subject');
                                setFormValue('subject', value);
                            } }
                        />
                    </FormGroup>
                </Col>
                <Col xs="12">
                    <FormGroup label={ t('Español') } margin="0px 0px 10px 0px">
                        <Input
                            value={ form.subject_es ?? '' }
                            onChange={ value => setFormValue('subject_es', value) }
                        />
                    </FormGroup>
                </Col>
                <Col xs="12">
                    <FormGroup label={ t('Inglés') } margin="0px 0px 10px 0px">
                        <Input
                            value={ form.subject_en ?? '' }
                            onChange={ value => setFormValue('subject_en', value) }
                        />
                    </FormGroup>
                </Col>
                <Text bold margin="0px 0px 10px 0px">
                    { t('Opciones') }
                </Text>
                <Col xs="12">
                    <Container flexDirection="row" gap="20px">
                        <Input
                            { ...optionInput }
                            width={ xs ? '200px' : '600px' }
                            onEnterPress={ handleOptionAdd }
                        />
                        <ExpandButton
                            circle
                            disabled={ !optionText.trim() }
                            icon="add"
                            size={ 40 }
                            onClick={ handleOptionAdd }
                        />
                    </Container>
                </Col>
                <Col xs="12">
                    <TagList
                        items={ parsedOptions }
                        onRemove={ ({ label }) => removeOption(label) }
                    />
                </Col>
            </Row>
            <ButtonFooter margin="20px 0px 0px 0px">
                <ExpandButton
                    caption={ t('Volver') }
                    size={ 100 }
                    onClick={ onToggle }
                />
                <ExpandButton
                    caption={ createMode ? t('Crear') : t('Confirmar') }
                    size={ 100 }
                    onClick={ onSubmit }
                />
            </ButtonFooter>
        </Modal>
    );
};

ConfigTemplateModal.propTypes = {
    active: PropTypes.bool,
    data: PropTypes.object,
    onToggle: PropTypes.func
};

export default ConfigTemplateModal;
